import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Bar,
} from 'recharts';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import type { TooltipProps } from 'recharts';

import type { KeywordTrendResponse, KeywordTrendWeeks } from '../../../types/report.js';
import styles from './KeywordDetailPanel.module.css';

const WEEK_OPTIONS: KeywordTrendWeeks[] = [4, 12, 55];

const formatWeekLabel = (iso: string) => {
  const date = new Date(iso);
  return date.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit' });
};

const formatNumber = (value: number) => new Intl.NumberFormat('cs-CZ').format(value);

const formatRangeLabel = (startIso: string) => {
  const start = new Date(startIso);
  const end = new Date(startIso);
  end.setDate(end.getDate() + 6);
  const startStr = start.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit' });
  const endStr = end.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit' });
  return `${startStr} – ${endStr}`;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(value);

const formatPercent = (value: number) => `${value.toFixed(1)} %`;

const formatRoas = (value: number | null) => {
  if (value === null) {
    return '—';
  }
  return `${(value * 100).toFixed(1)} %`;
};

const normalizeValue = (value: ValueType | null | undefined): number | null => {
  if (value === null || typeof value === 'undefined') {
    return null;
  }
  if (Array.isArray(value)) {
    const first = value[0];
    if (typeof first === 'number') {
      return first;
    }
    const parsed = Number(first);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (typeof value === 'number') {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const roasTooltipFormatter: TooltipProps<ValueType, NameType>['formatter'] = (value) => {
  const numeric = normalizeValue(value);
  if (numeric === null) {
    return ['—', 'ROAS'];
  }
  return [`${numeric.toFixed(1)} %`, 'ROAS'];
};

interface KeywordDetailPanelProps {
  target:
    | {
        campaignName: string;
        campaignId: string | null;
        adGroupName: string | null;
        adGroupId: string | null;
        keywordName: string;
        keywordId: string | null;
        orgId: string | null;
      }
    | null;
  weeks: KeywordTrendWeeks;
  onWeeksChange: (weeks: KeywordTrendWeeks) => void;
  query: {
    isFetching: boolean;
    isError: boolean;
    data?: KeywordTrendResponse;
    error?: Error | null;
  };
}

export const KeywordDetailPanel = ({ target, weeks, onWeeksChange, query }: KeywordDetailPanelProps) => {
  if (!target) {
    return <div className={styles.placeholder}>Vyberte řádek pro zobrazení detailu klíčového slova.</div>;
  }

  if (query.isError) {
    return (
      <div className={styles.stateError}>
        <strong>Chyba načtení detailu.</strong>
        <span>{query.error?.message ?? 'Zkuste to prosím znovu.'}</span>
      </div>
    );
  }

  if (query.isFetching && !query.data) {
    return <div className={styles.state}>Načítám detail…</div>;
  }

  const data = query.data;

  const displayCampaign = data?.campaignName ?? target.campaignName;
  const displayAdGroup = data?.adGroupName ?? target.adGroupName ?? null;
  const displayKeyword = data?.keywordName ?? target.keywordName;

  if (!data || data.points.length === 0) {
    return <div className={styles.placeholder}>Pro toto klíčové slovo zatím nemáme data.</div>;
  }

  const chartData = data.points.map((point) => {
    const roasPct = point.roas === null ? null : point.roas * 100;
    const ratioPct = Math.round(point.trialRatio * 1000) / 10;
    const cpiValue = point.usersCount > 0 ? point.spend / point.usersCount : null;

    return {
      weekStart: point.weekStart,
      label: formatWeekLabel(point.weekStart),
      rangeLabel: formatRangeLabel(point.weekStart),
      users: point.usersCount,
      revenue: point.totalRevenue,
      average: point.averageRevenue,
      spend: point.spend,
      roasPct,
      ratioPct,
      cpi: cpiValue,
    };
  });

  const totals = data.points.reduce(
    (acc, point) => {
      acc.users += point.usersCount;
      acc.revenue += point.totalRevenue;
      acc.spend += point.spend;
      acc.trials += point.trialCount;
      return acc;
    },
    { users: 0, revenue: 0, spend: 0, trials: 0 }
  );

  const summary = {
    users: totals.users,
    revenue: totals.revenue,
    average: totals.users > 0 ? Number((totals.revenue / totals.users).toFixed(2)) : 0,
    spend: totals.spend,
    roas: totals.spend > 0 ? totals.revenue / totals.spend : null,
    ratio:
      chartData.length > 0
        ? chartData.reduce((acc, point) => acc + point.ratioPct, 0) / chartData.length
        : 0,
    cpi: totals.users > 0 ? Number((totals.spend / totals.users).toFixed(2)) : null,
  };

  const isUpdating = query.isFetching;

  return (
    <div className={styles.panel} aria-busy={isUpdating}>
      {isUpdating ? (
        <div className={styles.loadingOverlay} aria-live="polite">
          Aktualizuji detail…
        </div>
      ) : null}
      <header className={styles.header}>
        <div>
          <h3 className={styles.title}>{displayKeyword}</h3>
          <p className={styles.subtitleDetail}>
            Kampaň: <strong>{displayCampaign}</strong>
            {displayAdGroup ? (
              <>
                {' · '}Ad group: <strong>{displayAdGroup}</strong>
              </>
            ) : null}
          </p>
          <p className={styles.subtitle}>
            Zobrazeno {weeks} týdnů · Aktualizováno{' '}
            {new Date(data.meta.generatedAt).toLocaleString('cs-CZ')}
          </p>
        </div>
        <div className={styles.weekSelector}>
          {WEEK_OPTIONS.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => onWeeksChange(option)}
              className={option === weeks ? styles.weekButtonActive : styles.weekButton}
            >
              {option} týdnů
            </button>
          ))}
        </div>
      </header>

      <section className={styles.chartSection}>
        <header className={styles.chartHeader}>
          <h4 className={styles.chartTitle}>Uživatelé &amp; Revenue</h4>
          <span className={styles.chartSummary}>
            {formatNumber(summary.users)} uživatelů · {formatCurrency(summary.revenue)} · Náklady{' '}
            {formatCurrency(summary.spend)}
          </span>
        </header>
        <div className={styles.chartWrapper}>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={chartData} margin={{ top: 20, right: 32, bottom: 16, left: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis
                yAxisId="counts"
                tick={{ fontSize: 12 }}
              axisLine={{ stroke: '#94a3b8' }}
              label={{ value: 'Počet', angle: -90, position: 'insideLeft', offset: 10 }}
            />
            <YAxis
              yAxisId="revenue"
              orientation="right"
              tick={{ fontSize: 12 }}
              axisLine={{ stroke: '#64748b' }}
              tickFormatter={(value) => `${value}`}
              label={{ value: 'Revenue', angle: 90, position: 'insideRight', offset: 10 }}
            />
              <Tooltip formatter={(value: number, name) => {
                if (name === 'Revenue') {
                  return [formatCurrency(value), name];
                }
              return [value, name];
            }}
              labelFormatter={(_, payload) => payload?.[0]?.payload.rangeLabel ?? ''}
            />
              <Legend />
              <Bar yAxisId="counts" dataKey="users" name="Uživatelé" fill="#60a5fa" radius={[4, 4, 0, 0]} />
              <Line yAxisId="revenue" dataKey="revenue" name="Revenue" stroke="#2563eb" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className={styles.chartSection}>
        <header className={styles.chartHeader}>
          <h4 className={styles.chartTitle}>Průměr na uživatele</h4>
          <span className={styles.chartSummary}>{formatCurrency(summary.average)}</span>
        </header>
        <div className={styles.chartWrapper} style={{ height: 240 }}>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData} margin={{ top: 20, right: 32, bottom: 16, left: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} axisLine={{ stroke: '#64748b' }} tickFormatter={(value) => `${value}`}
                label={{ value: 'Průměr (USD)', angle: -90, position: 'insideLeft', offset: 10 }}
              />
              <Tooltip
                formatter={(value: number) => [formatCurrency(value), 'Průměr']}
                labelFormatter={(_, payload) => payload?.[0]?.payload.rangeLabel ?? ''}
              />
              <Legend />
              <Line dataKey="average" name="Průměr" stroke="#0ea5e9" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className={styles.chartSection}>
        <header className={styles.chartHeader}>
          <h4 className={styles.chartTitle}>ROAS</h4>
          <span className={styles.chartSummary}>{formatRoas(summary.roas)}</span>
        </header>
        <div className={styles.chartWrapper} style={{ height: 240 }}>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData} margin={{ top: 20, right: 32, bottom: 16, left: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis
                tick={{ fontSize: 12 }}
                axisLine={{ stroke: '#f97316' }}
                tickFormatter={(value) => `${value.toFixed(1)} %`}
                label={{ value: 'ROAS %', angle: -90, position: 'insideLeft', offset: 10 }}
              />
              <Tooltip formatter={roasTooltipFormatter} labelFormatter={(_, payload) => payload?.[0]?.payload.rangeLabel ?? ''} />
              <Legend />
              <Line
                dataKey="roasPct"
                name="ROAS"
                stroke="#f97316"
                strokeWidth={2}
                dot={{ r: 3 }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className={styles.chartSection}>
        <header className={styles.chartHeader}>
          <h4 className={styles.chartTitle}>CPI</h4>
          <span className={styles.chartSummary}>
            {summary.cpi === null ? '—' : formatCurrency(summary.cpi)}
          </span>
        </header>
        <div className={styles.chartWrapper} style={{ height: 240 }}>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData} margin={{ top: 20, right: 32, bottom: 16, left: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis
                tick={{ fontSize: 12 }}
                axisLine={{ stroke: '#475569' }}
                tickFormatter={(value) => formatCurrency(value)}
                label={{ value: 'CPI (USD)', angle: -90, position: 'insideLeft', offset: 10 }}
              />
              <Tooltip
                formatter={(value: number) => [formatCurrency(value), 'CPI']}
                labelFormatter={(_, payload) => payload?.[0]?.payload.rangeLabel ?? ''}
              />
              <Legend />
              <Line dataKey="cpi" name="CPI" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className={styles.chartSection}>
        <header className={styles.chartHeader}>
          <h4 className={styles.chartTitle}>Trial / uživatelé</h4>
          <span className={styles.chartSummary}>{formatPercent(summary.ratio)}</span>
        </header>
        <div className={styles.chartWrapper} style={{ height: 240 }}>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData} margin={{ top: 20, right: 32, bottom: 16, left: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} axisLine={{ stroke: '#10b981' }} tickFormatter={formatPercent}
                label={{ value: 'Trial %', angle: -90, position: 'insideLeft', offset: 10 }}
              />
              <Tooltip
                formatter={(value: number) => [formatPercent(value), 'Trial %']}
                labelFormatter={(_, payload) => payload?.[0]?.payload.rangeLabel ?? ''}
              />
              <Legend />
              <Line dataKey="ratioPct" name="Trial %" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>
    </div>
  );
};
